import { useEffect, useState } from "react"

// This component is used to debounce the value of a variable. The typical use
// case is to debounse the loading state of a component, so that the loading
// spinner/skeleton doesn't make the content flicker unless the loading is
// actually taking a long time.

const DEFAULT_DELAY_IN_MS = 300

type UseDebounceValueProps<T> = {
  value: T
  delayInMs?: number
  callback?: (value: T) => void
}

export function useDebouncedValue<T>({
  value,
  delayInMs = DEFAULT_DELAY_IN_MS,
  callback,
}: UseDebounceValueProps<T>) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      callback?.(value)
      setDebouncedValue(value)
    }, delayInMs)

    return () => {
      clearTimeout(handler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delayInMs])

  return debouncedValue
}
